import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";

import { useSpamGuard } from "./useSpamGuard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMS_TEXT =
  "By checking this box, I consent to receive non-marketing text messages from Honest Water LLC related to account notifications, consultation confirmations, appointment reminders, service updates, and customer support. Message frequency varies. Message & data rates may apply. Text HELP for assistance. Reply STOP to opt out at any time.";
export const MARKETING_TEXT =
  "By checking this box, I consent to receive occasional marketing communications from Honest Water LLC. Message frequency varies (up to 2 messages per month). Message & data rates may apply. Text HELP for assistance. Reply STOP to opt out at any time.";

const emptyForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  message: "",
  smsConsent: false,
  marketingConsent: false,
};

export function getUtms() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
  };
}

export default function ContactForm() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const guard = useSpamGuard();

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };
  const handleChange = (e) => setField(e.target.name, e.target.value);

  const validate = () => {
    const er = {};
    if (!form.firstName.trim()) er.firstName = "Required";
    if (!form.lastName.trim()) er.lastName = "Required";
    if (!form.email.trim()) er.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.message.trim()) er.message = "Required";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        termsAgreed: true,
        source: "Website Contact Form",
        ...getUtms(),
        ...guard.fields(),
      };
      const { data } = await axios.post(`${API}/leads`, payload);
      if (data.success) {
        setForm(emptyForm);
        guard.reset();
        setSent(true);
      }
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Too many requests. Please call us or try again later." : "Something went wrong. Please call us or try again.");
      if (process.env.NODE_ENV === "development") console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-success" className="text-center py-10">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-white border border-[#344C59]">
          <CheckCircle2 className="w-8 h-8 text-[#344C59]" />
        </div>
        <h3 className="font-heading font-normal text-3xl text-[#172B38] mt-6">Message received</h3>
        <p className="text-[#3E474B] mt-3 leading-relaxed max-w-sm mx-auto">
          Thank you for reaching out. A member of our local family team will be in touch with you shortly.
        </p>
        <button onClick={() => setSent(false)} data-testid="contact-send-another" className="btn-outline mt-6">
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="w-full relative" noValidate>
      {guard.Honeypot}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="hw-label" htmlFor="c-first">First Name*</label>
          <input id="c-first" data-testid="contact-first-name" className="hw-input" name="firstName"
            value={form.firstName} onChange={handleChange} placeholder="Jane" />
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
        </div>
        <div>
          <label className="hw-label" htmlFor="c-last">Last Name*</label>
          <input id="c-last" data-testid="contact-last-name" className="hw-input" name="lastName"
            value={form.lastName} onChange={handleChange} placeholder="Doe" />
          {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="hw-label" htmlFor="c-phone">Phone Number</label>
          <input id="c-phone" data-testid="contact-phone" className="hw-input" name="phone" type="tel"
            value={form.phone} onChange={handleChange} placeholder="(571) 619-9876" />
        </div>
        <div>
          <label className="hw-label" htmlFor="c-email">Email Address*</label>
          <input id="c-email" data-testid="contact-email" className="hw-input" name="email" type="email"
            value={form.email} onChange={handleChange} placeholder="jane@email.com" />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
      </div>

      <div className="mt-4">
        <label className="hw-label" htmlFor="c-message">Message*</label>
        <textarea id="c-message" data-testid="contact-message" className="hw-input" name="message" rows={4}
          value={form.message} onChange={handleChange} placeholder="How can we help you?" />
        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
      </div>

      <div className="mt-8 border-t pt-6" style={{ borderColor: "#D9D6CC" }}>
        <h3 className="text-sm font-semibold text-[#3E474B] mb-4">SMS Communication Preferences</h3>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="contact-sms-consent" className="mt-1 w-4 h-4 accent-[#172B38] shrink-0"
              checked={form.smsConsent} onChange={(e) => setField("smsConsent", e.target.checked)} />
            <span className="text-xs text-[#5F676B] leading-relaxed">{SMS_TEXT}</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="contact-marketing-consent" className="mt-1 w-4 h-4 accent-[#172B38] shrink-0"
              checked={form.marketingConsent} onChange={(e) => setField("marketingConsent", e.target.checked)} />
            <span className="text-xs text-[#5F676B] leading-relaxed">{MARKETING_TEXT}</span>
          </label>
        </div>
      </div>

      <button type="submit" data-testid="contact-submit-button" className="btn-cta w-full mt-8" disabled={submitting}>
        {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : (<><Send className="w-4 h-4" /> Send Message</>)}
      </button>

      <p className="text-xs text-[#5F676B] mt-4 text-center leading-relaxed">
        By submitting this form, you acknowledge that you have read and agree to our{" "}
        <Link to="/privacy" className="text-[#172B38] underline">Privacy Policy</Link> and{" "}
        <Link to="/terms" className="text-[#172B38] underline">Terms &amp; Conditions</Link>.
      </p>
    </form>
  );
}
